import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import ActivityScreen from "../../../components/screens/Activity/ActivityScreen";
import {
  classrooms as classroomQuerys,
  completions as completionQuerys,
  progress as progressQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { DateTime } from "luxon";
import { getClassroom } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getClassroomCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import { getClassroomProgress } from "@nualang/nualang-api-and-queries/APIs/Progress";
import {
  completions as completionHelpers,
  progress as progressHelpers,
  classrooms as classroomHelpers,
} from "../../../utils/helpers";
import config from "../../../config";
import { useApp, useSubscription } from "../../../hooks";

function Classroom(props) {
  const [title, setTitle] = useState("");
  const [picture, setPicture] = useState("");
  const [description, setDescription] = useState("");
  const [filterName, setFilterName] = useState("");
  const [fromDate, setFromDate] = React.useState(
    DateTime.now().minus({ years: 1 }),
  );
  const [toDate, setToDate] = React.useState(DateTime.now());

  const {
    authData: { username, attributes },
    handleViewExerciseAttempt,
  } = props;
  const params = useParams();
  const { classroomId } = params;
  const { subscription } = useSubscription();
  const { markCommunityAccessRestricted } = useApp();
  const { t } = useTranslation();

  const classroomQuery = classroomQuerys.useClassroom(
    async (cId) => {
      const classroom = await getClassroom(cId);
      const formattedClassroom = await classroomHelpers.formatClassroom(
        classroom.Item,
        false,
        props.authData,
      );
      return formattedClassroom;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const classroom =
    classroomQuery.isSuccess &&
    classroomQuery.data &&
    classroomQuery.data.classroomId
      ? classroomQuery.data
      : {};

  // Fallback signal: this classroom may already be flagged by the backend as
  // community-access-restricted before the user's own record ever gets
  // flagged (see appAtoms.js). Learn about it as soon as we see it.
  useEffect(() => {
    markCommunityAccessRestricted([classroomQuery.data]);
  }, [classroomQuery.data, markCommunityAccessRestricted]);

  const isCreator =
    classroom.createdBy === username ||
    (classroom.collaborators &&
      Array.isArray(classroom.collaborators) &&
      (classroom.collaborators.includes(attributes.email) ||
        classroom.collaborators.includes(username)));

  const classroomCompletionsQuery = completionQuerys.useClassroomCompletions(
    async (classroomId, { fromDate, toDate }) => {
      const classroomCompletions = await getClassroomCompletions(classroomId);
      let formattedCompletions = await completionHelpers.formatCompletions(
        classroomCompletions.Items.filter(
          (item) => isCreator || item.memberId === username,
        ),
        username,
        attributes.preferred_username,
      );
      if (fromDate || filterName) {
        if (filterName !== "" && fromDate) {
          formattedCompletions = formattedCompletions.filter(
            (item) =>
              isCreator &&
              item.createdByName
                .toLowerCase()
                .includes(filterName.toLowerCase()) &&
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        } else if (!filterName && fromDate) {
          formattedCompletions = formattedCompletions.filter(
            (item) =>
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        }
      }
      return formattedCompletions.map((completion) => ({
        ...completion,
        classroomId,
      }));
    },
    {
      classroomId: classroomId,
      filters: {
        fromDate,
        toDate,
        filterName,
      },
    },
    { enabled: !!classroomId && classroomQuery.isSuccess },
  );

  const classroomProgressQuery = progressQuerys.useClassroomProgress(
    async (classroomId, { fromDate, toDate }) => {
      const classroomProgress = await getClassroomProgress(classroomId);
      let formattedProgress = await progressHelpers.formatProgress(
        classroomProgress.Items.filter(
          (item) => isCreator || item.memberId === username,
        ),
        username,
        attributes.preferred_username,
      );
      if (fromDate || filterName) {
        if (filterName !== "" && fromDate) {
          formattedProgress = formattedProgress.filter(
            (item) =>
              isCreator &&
              item.createdByName
                .toLowerCase()
                .includes(filterName.toLowerCase()) &&
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        } else if (!filterName && fromDate) {
          formattedProgress = formattedProgress.filter(
            (item) =>
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        }
      }
      return formattedProgress;
    },
    {
      classroomId: classroomId,
      filters: {
        fromDate,
        toDate,
        filterName,
      },
    },
    { enabled: !!classroomId && classroomQuery.isSuccess },
  );

  const fromDateChange = (date) => {
    setFromDate(date);
  };

  const toDateChange = (date) => {
    setToDate(date);
  };

  const filterChange = (filter) => {
    setFilterName(filter);
  };

  useEffect(() => {
    if (
      classroom?.description &&
      classroom?.classroomName &&
      classroom?.picture
    ) {
      setDescription(classroom.description);
      setTitle(classroom.classroomName);
      setPicture(classroom.picture);
    }
  }, [classroom?.description, classroom?.classroomName, classroom?.picture]);

  return (
    <ActivityScreen
      t={t}
      fromDateChange={fromDateChange}
      isCreator={isCreator}
      fromDate={fromDate}
      toDate={toDate}
      toDateChange={toDateChange}
      filterChange={filterChange}
      completions={
        classroomCompletionsQuery.isSuccess &&
        Array.isArray(classroomCompletionsQuery.data) &&
        classroomCompletionsQuery.data.length
          ? classroomCompletionsQuery.data
          : []
      }
      completionsTotal={
        classroomCompletionsQuery.isSuccess &&
        Array.isArray(classroomCompletionsQuery.data) &&
        classroomCompletionsQuery.data.length
          ? classroomCompletionsQuery.data.length
          : 0
      }
      progress={
        classroomProgressQuery.isSuccess &&
        Array.isArray(classroomProgressQuery.data) &&
        classroomProgressQuery.data.length
          ? classroomProgressQuery.data
          : []
      }
      progressTotal={
        classroomProgressQuery.isSuccess &&
        Array.isArray(classroomProgressQuery.data) &&
        classroomProgressQuery.data.length
          ? classroomProgressQuery.data.length
          : 0
      }
      handleViewEvent={handleViewExerciseAttempt}
      title={title}
      description={description}
      picture={picture}
      subscription={subscription}
      isCompletionsLoading={classroomCompletionsQuery.isLoading}
      isProgressLoading={classroomProgressQuery.isLoading}
      mainSiteUrl={config.SITE_URL}
    />
  );
}

Classroom.propTypes = {
  authData: PropTypes.object,
  themeContext: PropTypes.object,
  handleViewExerciseAttempt: PropTypes.func,
};

export default Classroom;
